import { Box, Text, Heading, theme, Container } from '@everlywell/ui-kit';
import VideoComponent from 'components/VideoComponent';
import React from 'react';
import renderRichText, {
  ContentfulRichText,
} from 'utils/contentful/renderRichText';

// Types ----------

export interface VideoType {
  file: {
    url: string;
    contentType: string;
  };
  description?: string;
}

export interface VideoGridCardProps {
  video: VideoType;
  title: string;
  description: ContentfulRichText;
  imageStyles?: React.CSSProperties;
  videoProps?: {
    loop?: boolean;
    autoPlay?: boolean;
    muted?: boolean;
    controls?: boolean;
    playsInline?: boolean;
    objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
    objectPosition?: string;
    height?: string | number;
    width?: string | number;
  };
}

// VideoGridCard ------

const VideoGridCard: React.FC<VideoGridCardProps> = (props) => {
  const { video, title, description, imageStyles, videoProps = {} } = props;

  const htmlVideoProps: React.VideoHTMLAttributes<HTMLVideoElement> = {
    loop: videoProps.loop ?? true,
    autoPlay: videoProps.autoPlay ?? true,
    muted: videoProps.muted ?? true,
    playsInline: videoProps.playsInline ?? true,
    controls: videoProps.controls ?? false,
    style: {
      width: videoProps.width ?? '100%',
      height: videoProps.height ?? '100%',
      objectFit: videoProps.objectFit ?? 'cover',
      objectPosition: videoProps.objectPosition ?? 'center',
    },
  };

  return (
    <Container size="fluid">
      {/* Video aspect ratio to be 3:2 from Figma design */}
      <Box
        borderRadius={'xl'}
        display="flex"
        marginBottom={6}
        width="100%"
        overflow="hidden"
      >
        {/* Video as a component for screen readers */}
        <Box
          borderRadius={'xl'}
          height={videoProps.height ?? '100%'}
          width="100%"
          sx={{
            ...imageStyles,
            '& video': {
              width: '100%',
              height: '100%',
              objectFit: videoProps.objectFit ?? 'cover',
              objectPosition: videoProps.objectPosition ?? 'center',
            },
          }}
        >
          <VideoComponent
            url={video.file.url}
            contentType={video.file.contentType}
            htmlVideoProps={htmlVideoProps}
          />
        </Box>
      </Box>
      <Heading size="sm" marginBottom={theme.space[2]}>
        {title}
      </Heading>
      <Text
        size="lg"
        as="div"
        color="tints.darkGrey"
        sx={{
          '@media (max-width: 768px)': {
            maxWidth: '280px',
          },
        }}
      >
        {renderRichText(description)}
      </Text>
    </Container>
  );
};

// Export ---------

export default React.memo(VideoGridCard);
